import {
  AccountGroupOutline,
  SchoolOutline,
  HeadSnowflakeOutline,
  AccountMultiplePlus,
  FileMultiple,
  HomeAnalytics,
  ClipboardTextClock,
  MapMarkerRadius,
  AccountCogOutline,
  AccountSupervisor,
  CreditCardOutline,
  HomeOutline,
  AccountBox,
  ClipboardAccountOutline,
  AccountDetailsOutline,
  FaceAgent,
  HomeSearchOutline,
  Sync,
  MonitorDashboard,
  CheckAll,
  PercentOutline,
  CashMultiple,
  Receipt,
  CurrencyEur,
  AlignVerticalBottom,
  RepeatOnce,
  NotebookMultiple,
  FrequentlyAskedQuestions,
  CompassOutline,
  ChartTimeline,
  AccountAlertOutline,
  MagnifyPlusOutline,
  FileDocumentOutline,
  ChartLineVariant,
  AccountMultiple,
  ThumbUpOutline,
  AccountSwitch,
  Bank,
  ClipboardTextOutline,
  ShieldPlusOutline,
  Domain,
  City,
  CheckboxMultipleMarked,
  Cpu64Bit
} from 'mdi-material-ui'
import { ExamAttendance } from 'src/components/icons/examAttendance'
import { GenerateQuote } from 'src/components/icons/generateQuotes'
import { GenerateReggieNumber } from 'src/components/icons/generateReggieNumber'
import { ManageRollover } from 'src/components/icons/manageRollover'
import { OfflineFeesApprovalTabIcon } from 'src/components/icons/OfflineDashboardIcons'
import { Vip } from 'src/components/icons/vip'
import { CommonService } from 'src/service'

// Registry mapping icon name strings (from API) to actual React icon components
const iconRegistry: Record<string, any> = {
  AccountGroupOutline,
  SchoolOutline,
  HeadSnowflakeOutline,
  AccountMultiplePlus,
  FileMultiple,
  HomeAnalytics,
  ClipboardTextClock,
  MapMarkerRadius,
  AccountCogOutline,
  AccountSupervisor,
  CreditCardOutline,
  HomeOutline,
  AccountBox,
  ClipboardAccountOutline,
  AccountDetailsOutline,
  FaceAgent,
  HomeSearchOutline,
  Sync,
  MonitorDashboard,
  CheckAll,
  PercentOutline,
  CashMultiple,
  Receipt,
  CurrencyEur,
  AlignVerticalBottom,
  RepeatOnce,
  NotebookMultiple,
  FrequentlyAskedQuestions,
  CompassOutline,
  ChartTimeline,
  AccountAlertOutline,
  MagnifyPlusOutline,
  FileDocumentOutline,
  ChartLineVariant,
  AccountMultiple,
  ThumbUpOutline,
  AccountSwitch,
  Bank,
  ClipboardTextOutline,
  ShieldPlusOutline,
  Domain,
  City,
  GenerateQuote,
  GenerateReggieNumber,
  ManageRollover,
  Vip,
  CheckboxMultipleMarked,
  OfflineFeesApprovalTabIcon,
  Cpu64Bit,
  ExamAttendance
}

/**
 * Resolves an icon name string to the actual icon component.
 * Falls back to HomeOutline if the icon is not found in the registry.
 */
const resolveIcon = (iconName: string): any => {
  return iconRegistry[iconName] || HomeOutline
}

// Identifier for the current application's menu group.
// Paths under this group are used as-is (no baseUrl or prefix needed).
const CURRENT_APP_MENU_GROUP = 'operationMenu'

/**
 * Returns the URL path prefix for a given menu group key.
 * Each menu group maps to a different UI application with its own route prefix.
 */
const getMenuGroupPrefix = (groupKey: string): string => {
  switch (groupKey) {
    case 'enrolmentMenu':
      return '/enrolment'
    case 'academicMenu':
      return '/academic'
    case 'financeMenu':
      return '/finance'
    case 'assessmentMenu':
      return '/assessment'
    case 'corporateMenu':
      return '/corporate'
    case 'userManagementMenu':
      return '/user-management'
    case 'misMenu':
      return '/operation/mis-report'
    default:
      return ''
  }
}

/**
 * Resolves a path based on the menu group it belongs to.
 * - Current app (operationMenu): path is used as-is
 * - Other apps: baseUrl + prefix + path
 */
const resolvePath = (path: string, baseUrl: string, groupKey: string): string => {
  if (!path) return path

  // Current app — no prefix or baseUrl needed
  if (groupKey === CURRENT_APP_MENU_GROUP) {
    return path
  }

  // Other apps — prepend baseUrl + group prefix
  const prefix = getMenuGroupPrefix(groupKey)

  return `${baseUrl}${prefix}${path}`
}

/**
 * Transforms a raw menu item from the API into a format the frontend expects.
 * - Maps icon strings to components
 * - Resolves paths based on the menu group (current app vs other apps)
 */
const transformMenuItem = (item: any, baseUrl: string, groupKey: string): any => {
  const transformed: any = {
    title: item.title,
    icon: resolveIcon(item.icon),
    module: item.module
  }

  if (item.path) {
    transformed.path = resolvePath(item.path, baseUrl, groupKey)
  }

  if (item.badgeColor) {
    transformed.badgeColor = item.badgeColor
  }

  if (item.children && Array.isArray(item.children)) {
    transformed.children = item.children.map((child: any) => ({
      title: child.title,
      path: resolvePath(child.path, baseUrl, groupKey)
    }))
  }

  return transformed
}

/**
 * Transforms a raw menu group from the API (keyed by feature code) into
 * the format the frontend expects.
 */
const transformMenuGroup = (menuGroup: Record<string, any>, baseUrl: string, groupKey: string): Record<string, any> => {
  const result: Record<string, any> = {}
  for (const [featureCode, menuItem] of Object.entries(menuGroup)) {
    result[featureCode] = transformMenuItem(menuItem, baseUrl, groupKey)
  }

  return result
}

// SessionStorage key for caching the menu config
const MENU_CONFIG_CACHE_KEY = 'menuConfig'

/**
 * Transforms raw API data (with string icon names) into the format the frontend
 * expects (with React components and resolved paths).
 * - operationMenu paths are used as-is (current app)
 * - Other menu group paths get baseUrl + prefix prepended
 * This is separated so it can be applied both to fresh API responses and cached data.
 */
const transformRawConfig = (rawData: Record<string, any>): Record<string, any> => {
  const baseUrl = process.env.NEXT_PUBLIC_USER_REDIRECT_URL || ''
  const transformed: Record<string, any> = {}

  for (const [groupKey, groupMenus] of Object.entries(rawData)) {
    transformed[groupKey] = transformMenuGroup(groupMenus as Record<string, any>, baseUrl, groupKey)
  }

  return transformed
}

/**
 * Fetches the menu config from the backend API.
 * Caches the result in sessionStorage so it survives page refreshes
 * within the same tab, and is automatically cleared when the tab closes.
 *
 * @returns The full menu config object with all menu groups, or null if the fetch fails.
 */
export const fetchMenuConfig = async (): Promise<Record<string, any> | null> => {
  // Cannot run on the server — CommonService uses localStorage for auth tokens
  if (typeof window === 'undefined') {
    return null
  }

  // No auth token means user is not logged in — skip the API call to avoid
  // triggering the 401 error interceptor which would redirect to login page
  const accessToken = window.localStorage.getItem('accessToken')
  if (!accessToken) {
    return null
  }

  const userData = window.localStorage.getItem('userData')
  if (!userData || !JSON.parse(userData)?.email) {
    return null
  }

  // Check sessionStorage for cached raw API data
  const cached = window.sessionStorage.getItem(MENU_CONFIG_CACHE_KEY)
  if (cached) {
    try {
      const rawData = JSON.parse(cached)

      return transformRawConfig(rawData)
    } catch {
      // Corrupted cache — clear and re-fetch
      window.sessionStorage.removeItem(MENU_CONFIG_CACHE_KEY)
    }
  }

  try {
    const response = await CommonService.getMenuConfig()

    if (response?.data?.data && typeof response.data.data === 'object' && Object.keys(response.data.data).length > 0) {
      window.sessionStorage.setItem(MENU_CONFIG_CACHE_KEY, JSON.stringify(response.data.data))

      return transformRawConfig(response.data.data)
    }

    return null
  } catch (error) {
    console.error('Failed to fetch menu config from API, falling back to hardcoded menus:', error)

    return null
  }
}

/**
 * Resets the cached menu config. Useful for testing or when the config changes.
 */
export const resetMenuConfigCache = () => {
  window.sessionStorage.removeItem(MENU_CONFIG_CACHE_KEY)
}
